/**
 * BOE Package Pattern Config データ変換ロジック
 *
 * APIレスポンスとフロントエンド型の相互変換を行う純粋関数群
 *
 * 設計方針:
 * - 純粋関数のみ（副作用なし）
 * - React/React Queryに依存しない
 * - テスト容易性を重視
 */

import type { ApprovalTaskConfigFlow } from '@/features/boe-task/components/ApprovalTaskConfig/ApprovalTaskConfig';
import type { ApprovalTaskConfig, TaskConfig, TaskConfigItem, TodoTaskConfig } from '@/features/boe-task/types/task-config';
import type { ApiResponseBody } from '@/lib/api/api-type-helper';

/**
 * パッケージパターン詳細のAPIレスポンス型
 */
export type PackagePatternDetailResponse = ApiResponseBody<'/v2/boe/package/{ownerProduct}/configs/{packagePatternId}', 'get'>;
type TaskChecklist = PackagePatternDetailResponse['steps'][number]['tasks'][number]['checklist'];
type TaskChecklistItem = NonNullable<TaskChecklist>[number];
function isTodoChecklistItem(checklistItem: TaskChecklistItem): checklistItem is TodoTaskConfig['checklist'][number] {
  if (typeof checklistItem !== 'object' || checklistItem === null) {
    return false;
  }
  if (!('id' in checklistItem) || !('label' in checklistItem) || !('order' in checklistItem)) {
    return false;
  }
  return typeof checklistItem.id === 'string' && typeof checklistItem.label === 'string' && typeof checklistItem.order === 'number';
}
function toTodoChecklist(checklist: TaskChecklist): TodoTaskConfig['checklist'] {
  return (checklist ?? []).filter(isTodoChecklistItem);
}

/**
 * フロントエンドのApprovalFlowをバックエンド用のstages配列に変換
 *
 * start/endノードを除外し、peopleGroupノードのみをstageとして抽出します。
 *
 * @param flow - フロントエンドのApprovalTaskConfigFlow構造
 * @returns バックエンドに送信するstages配列
 *
 * @example
 * ```typescript
 * const flow = {
 *   id: "start",
 *   type: "start",
 *   children: [
 *     {
 *       id: "node-1",
 *       type: "peopleGroup",
 *       peopleGroup: { id: "pg-1", name: "営業部" },
 *       children: [{ id: "end", type: "end" }]
 *     }
 *   ]
 * };
 * const stages = transformFlowToStages(flow);
 * // => [{ stageIndex: 0, peopleGroupId: "pg-1", requiredApprovals: 1 }]
 * ```
 */
export const transformFlowToStages = (flow: ApprovalTaskConfigFlow): Array<{
  stageIndex: number;
  peopleGroupId: string;
  requiredApprovals: number;
  designationMode?: 'none' | 'optional' | 'required';
}> => {
  if (flow.type !== 'start' || !flow.children) {
    return [];
  }
  const stages: Array<{
    stageIndex: number;
    peopleGroupId: string;
    requiredApprovals: number;
    designationMode?: 'none' | 'optional' | 'required';
  }> = [];
  let stageIndex = 0;
  const traverse = (node: ApprovalTaskConfigFlow) => {
    if (node.type === 'end' || node.type === 'start') {
      return;
    }
    if (node.type === 'peopleGroup' && node.peopleGroup) {
      stages.push({
        stageIndex: stageIndex++,
        peopleGroupId: node.peopleGroup.id,
        requiredApprovals: 1,
        designationMode: node.designationMode
      });
      if (node.children) {
        for (const child of node.children) {
          traverse(child);
        }
      }
    }
  };
  for (const child of flow.children) {
    traverse(child);
  }
  return stages;
};

/**
 * APIの承認フローデータをApprovalTaskConfigFlow型に変換
 *
 * @param apiFlowNodes - APIから返される承認フローノード配列
 * @returns フロントエンド用のApprovalTaskConfigFlow構造
 */
const transformApiFlowToApprovalFlow = (apiFlowNodes: PackagePatternDetailResponse['steps'][number]['tasks'][number]['flow'], designationMode?: 'none' | 'optional' | 'required'): ApprovalTaskConfigFlow => {
  if (!apiFlowNodes || apiFlowNodes.length === 0) {
    return {
      id: 'start-default',
      type: 'start',
      children: [{
        id: 'end-default',
        type: 'end'
      }]
    };
  }
  const convertApiNode = (apiNode: NonNullable<PackagePatternDetailResponse['steps'][number]['tasks'][number]['flow']>[number], index: number): ApprovalTaskConfigFlow => {
    return {
      id: apiNode.id,
      type: 'peopleGroup',
      // API 由来の id はエンジン Node の ID（保存済み）。段階数変更で
      // ローカル生成されるノードと区別し、Node 単位の設定（通知など）の可否判定に使う
      isPersisted: true,
      peopleGroup: apiNode.peopleGroup ? {
        id: apiNode.peopleGroup.id,
        name: apiNode.peopleGroup.name
      } : undefined,
      designationMode: apiNode.designationMode ?? designationMode,
      children: apiNode.children.length > 0 ? apiNode.children.map(convertApiNode) : [{
        id: `end-${index}`,
        type: 'end'
      }]
    };
  };
  return {
    id: 'start-api',
    type: 'start',
    children: apiFlowNodes.map(convertApiNode)
  };
};

/**
 * APIのTaskデータをTaskConfig型に変換
 *
 * @param task - APIから返されるタスクデータ
 * @returns フロントエンド用のTaskConfig
 */
export const transformApiTaskToTaskConfig = (task: PackagePatternDetailResponse['steps'][number]['tasks'][number], designationMode?: 'none' | 'optional' | 'required', nodeId?: string): TaskConfig | ApprovalTaskConfig | TodoTaskConfig => {
  const baseOperationTaskCatalog = task.baseOperationTaskCatalog || task;
  const baseTask: TaskConfig = {
    // baseOperationTaskCatalogが存在する場合はbaseOperationTaskCatalogのidを使用
    // Request Taskの場合、ConfigはBaseに対して行う必要があるため。
    id: baseOperationTaskCatalog?.id,
    taskType: task.taskType,
    taskName: task.name,
    description: task.description || '',
    inUse: task.inUse,
    nodeId,
    canConfirm: task.canConfirm,
    confirmationEnabled: task.confirmationEnabled,
    ...(task.freeForm ? {
      freeForm: {
        name: task.freeForm.name,
        description: task.freeForm.description,
        questions: task.freeForm.questions
      }
    } : {}),
    taskConfigItems: (baseOperationTaskCatalog.taskConfigItems || []).map((item, index): TaskConfigItem => ({
      id: `${baseOperationTaskCatalog.id}-item-${index}`,
      isSystemRequired: item.isSystemRequired,
      name: item.name,
      inUse: item.useSetting === 'used',
      taskCatalogId: baseOperationTaskCatalog.id,
      code: item.code
    }))
  };

  // タスクタイプ別の特定プロパティ追加
  if (task.taskType === 'Approval') {
    // Approvalタスクの場合はtask.idを使用（インスタンス固有）
    const taskWithId: TaskConfig = {
      ...baseTask,
      id: task.id
    };
    return {
      ...taskWithId,
      taskType: 'Approval',
      flow: transformApiFlowToApprovalFlow(task.flow || [], designationMode)
    };
  }
  if (task.taskType === 'Todo') {
    // Todoタスクの場合はtask.idを使用（インスタンス固有）
    return {
      ...baseTask,
      id: task.id,
      taskType: 'Todo',
      checklist: toTodoChecklist(task.checklist)
    };
  }

  // Request/Operationタスクの場合はbaseOperationTaskCatalog.idを使用（設定対象）
  return baseTask;
};